/**
 * Imported clip → authored keys — the conversion that puts a retargeted
 * animation INTO the Document (ADR-006 §13.4, ADR-008 §(g)). Until it runs, an
 * imported clip lives as a `clipSelection` (a reference: source + take +
 * profile) that the mixer plays and the Document knows nothing about: the
 * strip has no keys to draw, the `key.*` verbs have nothing to edit, and an
 * exporter has no single truth to read. After it runs, all three work on the
 * same object, with undo over it, because it is an ordinary authored clip.
 *
 * This module is a PURE reshaping that produces a command; it never touches a
 * Document (that is `applyCommand`'s one door) and never runs a retarget. It
 * carries the one fact the caller must not be allowed to miss: a v1 key track
 * is ROTATION-ONLY, so translation, scale and morph channels do not survive —
 * and a walk cycle's forward travel lives in a translation track. Every
 * dropped channel is named in the report, and a DECLARED root-motion channel
 * that gets dropped is called out separately, because that one changes what
 * the animation looks like rather than merely what it contains.
 * Depends on: ir/clip-data (the source shape), ir/errors, session/commands.
 * Depended on by: the app's import surface, the Document driver.
 */
import type { ClipData, TrackPath } from '../ir/clip-data.js';
import { CommandRefusal } from '../ir/errors.js';
import { parseCommand, type Command } from './commands.js';

/**
 * One channel the authored representation cannot hold — and what was actually
 * IN it. The measurements are the point: a channel's NAME does not tell you the
 * consequence of losing it. This report first said a dropped root-motion track
 * meant "the character animates in place instead of travelling", which is false
 * for the repo's own golden specimen — the Mixamo walk is a treadmill walk with
 * net hip displacement pinned at zero (`adapter-three/src/specimen.test.ts`
 * §"GROUND TRUTH"), so what its `Hip/translation` track carries is ~7.7 cm of
 * vertical gait bob and that is what dropping it costs. A category was read as
 * a quantity. Now the quantity is measured and travels with the loss.
 */
export interface DroppedChannel {
  readonly target: string;
  readonly path: TrackPath;
  readonly keys: number;
  /**
   * Per-component displacement from the FIRST key to the LAST — what a loop
   * does NOT accumulate. Near-zero on every axis means the channel returns
   * where it started (a cycle), however much it moved in between.
   */
  readonly netDelta: readonly number[];
  /**
   * Per-component peak-to-peak range across the whole channel — how much it
   * moved at all. Large amplitude with zero net delta is exactly a gait bob or
   * an in-place cycle; the two numbers are only meaningful together.
   */
  readonly amplitude: readonly number[];
}

/**
 * What the conversion did and what it could not do. Reported, never logged and
 * forgotten: a caller that ignores this ships an animation quietly missing a
 * channel and has no way to know (a green suite proves behaviour unchanged, not
 * correct).
 */
export interface MaterializeReport {
  readonly clipName: string;
  readonly bones: number;
  readonly keys: number;
  readonly droppedTracks: readonly DroppedChannel[];
  /**
   * The bone whose DECLARED root-motion channel did not survive, or null. Named
   * separately because the declaration is a fact about the clip's intent — but
   * read `droppedTracks` for what the channel actually held. A declared root
   * motion that never travels is a cycle, not a loss of travel.
   */
  readonly rootMotionLost: string | null;
}

export interface MaterializeResult {
  /** Ready for `dispatch`/`applyCommand` — gated by `parseCommand` already. */
  readonly command: Command;
  readonly report: MaterializeReport;
}

export interface MaterializeOptions {
  /**
   * The authored clip's name — its identity. Defaults to the ClipData's name;
   * pass one explicitly when that name is already taken (materializing twice
   * refuses, by design: clip names are identities).
   */
  readonly name?: string;
}

const ROTATION_STRIDE = 4;

/** Components per key, by channel — the stride ClipData's own gate enforces. */
function strideOf(path: TrackPath): number {
  return path === 'rotation' ? 4 : path === 'morph' ? 1 : 3;
}

/**
 * What a channel actually did: how far it ENDED from where it started, and how
 * far it ranged along the way. Both, because either alone misleads — zero net
 * delta with large amplitude is a cycle (the golden walk's 7.7 cm hip bob),
 * and equal net delta and amplitude is a one-way move.
 */
function measureChannel(track: ClipData['tracks'][number]): {
  netDelta: number[];
  amplitude: number[];
} {
  const stride = strideOf(track.path);
  const n = track.times.length;
  const netDelta: number[] = [];
  const amplitude: number[] = [];
  for (let c = 0; c < stride; c += 1) {
    const first = track.values[c] as number;
    const last = track.values[(n - 1) * stride + c] as number;
    let min = Infinity;
    let max = -Infinity;
    for (let k = 0; k < n; k += 1) {
      const v = track.values[k * stride + c] as number;
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
    netDelta.push(last - first);
    amplitude.push(max - min);
  }
  return { netDelta, amplitude };
}

/** The largest absolute component — the one number a status line has room for. */
function peak(values: readonly number[]): number {
  return values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
}

/** Below this, a channel's net displacement is a round-trip, not a move. The
 *  specimen gate uses the same 1e-3 to pin the golden walk as travel-free. */
const CYCLE_EPSILON = 1e-3;

/**
 * Reshape a retargeted clip into a `clip.materialize` command plus the report
 * of what was left behind. Refuses (rather than producing an empty clip) when
 * the source carries no rotation at all — an authored clip with no keys is
 * indistinguishable from a materialize that silently did nothing.
 */
export function materializeClip(clip: ClipData, options: MaterializeOptions = {}): MaterializeResult {
  const name = options.name ?? clip.name;

  const rotationTracks = clip.tracks.filter((t) => t.path === 'rotation');
  const droppedTracks: DroppedChannel[] = clip.tracks
    .filter((t) => t.path !== 'rotation')
    .map((t) => ({ target: t.target, path: t.path, keys: t.times.length, ...measureChannel(t) }));

  if (rotationTracks.length === 0) {
    const carried = clip.tracks.map((t) => `${t.target}/${t.path}`).join(', ');
    throw new CommandRefusal(
      `materializeClip "${clip.name}": no rotation tracks to author — ` +
        `a v1 key track is rotation-only and this clip carries ${carried || '(nothing)'} (§8).`,
    );
  }

  const tracks = rotationTracks.map((t) => {
    const keys = [];
    for (let i = 0; i < t.times.length; i += 1) {
      const o = i * ROTATION_STRIDE;
      keys.push({
        time: t.times[i] as number,
        rotation: {
          x: t.values[o] as number,
          y: t.values[o + 1] as number,
          z: t.values[o + 2] as number,
          w: t.values[o + 3] as number,
        },
      });
    }
    return { bone: t.target, keys };
  });

  // A declared root-motion channel is consumed from TRANSLATION only (the
  // ClipData gate enforces that), so if root motion was declared at all, this
  // conversion is dropping it. Named on its own because the consequence is
  // visible: the character walks in place.
  const rootMotionLost =
    clip.rootMotionTarget !== null && droppedTracks.some((d) => d.target === clip.rootMotionTarget && d.path === 'translation')
      ? clip.rootMotionTarget
      : null;

  const command = parseCommand({
    schemaVersion: '1.0.0',
    verb: 'clip.materialize',
    params: { name, tracks },
  });

  return {
    command,
    report: {
      clipName: name,
      bones: tracks.length,
      keys: tracks.reduce((n, t) => n + t.keys.length, 0),
      droppedTracks,
      rootMotionLost,
    },
  };
}

/**
 * The report as one line for a status surface. Says the loss FIRST when there
 * is one — a count of what arrived reads as success and buries what did not.
 *
 * It describes what was MEASURED in the lost channel, never what its name
 * implies. The first version of this line read "so this plays in place" off the
 * mere presence of a declared root-motion channel; on the repo's golden walk
 * that was doubly wrong — the clip already played in place (net travel pinned
 * at zero), and the thing actually lost was its vertical bob.
 */
export function describeMaterialize(report: MaterializeReport): string {
  const carried = `${report.keys} keys across ${report.bones} bones into "${report.clipName}"`;
  const root = report.droppedTracks.find(
    (d) => d.target === report.rootMotionLost && d.path === 'translation',
  );
  if (root !== undefined) {
    const travel = peak(root.netDelta);
    const moved = peak(root.amplitude);
    const what =
      travel < CYCLE_EPSILON
        ? // A cycle: it ends where it began. Saying "lost its travel" here would
          // be a lie, and saying nothing would hide a real loss of motion.
          `it travels nowhere (net ${travel.toExponential(1)}) but ranges ${moved.toFixed(1)} — ` +
          `that in-place motion is what goes flat`
        : `it travels ${travel.toFixed(1)} — that displacement is what goes flat`;
    return `root motion on "${report.rootMotionLost}" did NOT survive (authored clips are rotation-only): ${what}. ${carried}`;
  }
  if (report.rootMotionLost !== null) {
    return `root motion on "${report.rootMotionLost}" did NOT survive — authored clips are rotation-only. ${carried}`;
  }
  if (report.droppedTracks.length > 0) {
    const names = report.droppedTracks
      .map((d) => `${d.target}/${d.path} (ranges ${peak(d.amplitude).toFixed(1)})`)
      .join(', ');
    return `${carried}; dropped ${report.droppedTracks.length} non-rotation channel(s): ${names}`;
  }
  return carried;
}
